/**
 * Pure helpers for the reprice-at-submit (409 `price_changed`) flow,
 * shared by the FAMILY checkout and the SCHOOL forecast submit/edit.
 * Framework-free (no React, no `@/api` runtime imports) so the Zustand
 * stores, the dialog component, and the unit tests can all consume the
 * same logic.
 *
 * The server contract (same shape from both
 * `apps/ordo/server/src/routes/family-orders.ts` and
 * `apps/ordo/server/src/routes/school-orders.ts`):
 *
 *   409 { error: 'price_changed', changes: [{ menuItemId, oldCents, newCents }] }
 *   422 { error: 'price_missing', menuItemIds: [...] }
 */

/**
 * The slice of a cart line the reprice view needs. Both
 * `FamilyCartLine` and `SchoolCartLine` satisfy it structurally.
 */
export interface RepriceableLine {
  menuItemId: string
  name: string
  quantity: number
  unitPriceCents: number | null
  currency: string | null
}

/** One drifted line off the 409 `price_changed` body. */
export interface PriceChangeWire {
  menuItemId: string
  oldCents: number
  newCents: number
}

/** Display row for the PriceChangedDialog. */
export interface PriceChangeLineView {
  menuItemId: string
  name: string
  oldCents: number
  newCents: number
}

export interface PriceChangeView {
  lines: PriceChangeLineView[]
  /** True when every drifted line got CHEAPER — drives the friendlier copy. */
  allDecreased: boolean
  /**
   * Projected order total (cents) across the still-in-cart lines with
   * the fresh prices applied. Null when no line carries a price at all.
   */
  newTotalCents: number | null
  currency: string
}

/** Parse the `changes` array off an unknown 409 body. */
export function parsePriceChanges(body: unknown): PriceChangeWire[] {
  if (!body || typeof body !== 'object') {
    return []
  }
  const raw = (body as { changes?: unknown }).changes
  if (!Array.isArray(raw)) {
    return []
  }
  const out: PriceChangeWire[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue
    }
    const record = entry as Record<string, unknown>
    if (
      typeof record['menuItemId'] === 'string' &&
      typeof record['oldCents'] === 'number' &&
      typeof record['newCents'] === 'number'
    ) {
      out.push({ menuItemId: record['menuItemId'], oldCents: record['oldCents'], newCents: record['newCents'] })
    }
  }
  return out
}

/** Parse the `menuItemIds` array off an unknown 422 `price_missing` body. */
export function parseUnpricedMenuItemIds(body: unknown): string[] {
  if (!body || typeof body !== 'object') {
    return []
  }
  const raw = (body as { menuItemIds?: unknown }).menuItemIds
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.filter((id): id is string => typeof id === 'string')
}

/** Map menuItemId → fresh unit price, for the cart-store price patch. */
export function newPriceByMenuItemId(changes: readonly PriceChangeWire[]): Map<string, number> {
  return new Map(changes.map((c) => [c.menuItemId, c.newCents]))
}

/**
 * Build the dialog view: resolve item names off the cart lines, decide
 * increase-vs-decrease copy, and project the post-reprice order total
 * over the lines still in the cart.
 */
export function buildPriceChangeView(
  changes: readonly PriceChangeWire[],
  lines: readonly RepriceableLine[],
): PriceChangeView {
  const nameByMenuItemId = new Map<string, string>()
  for (const line of lines) {
    if (!nameByMenuItemId.has(line.menuItemId)) {
      nameByMenuItemId.set(line.menuItemId, line.name)
    }
  }
  const views: PriceChangeLineView[] = changes.map((c) => ({
    menuItemId: c.menuItemId,
    name: nameByMenuItemId.get(c.menuItemId) ?? 'Menu item',
    oldCents: c.oldCents,
    newCents: c.newCents,
  }))
  const priceById = newPriceByMenuItemId(changes)
  let total = 0
  let priced = false
  for (const line of lines) {
    if (line.quantity <= 0) {
      continue
    }
    const unit = priceById.get(line.menuItemId) ?? line.unitPriceCents
    if (unit != null) {
      total += unit * line.quantity
      priced = true
    }
  }
  return {
    lines: views,
    allDecreased: views.length > 0 && views.every((v) => v.newCents < v.oldCents),
    newTotalCents: priced ? total : null,
    currency: lines.find((l) => l.currency)?.currency ?? 'USD',
  }
}
